import styles from "../EventSubmissionForm/EventSubmissionForm.module.scss";
import i18n from "../../i18n";
import PropTypes from "prop-types";
import React, {useState, useEffect} from "react";
import {Form, Formik} from "formik";
import InputGroup from "../InputGroup";
import useLocalStorage from "./useLocalStorage";
import {InputGroupV2, PrevAndSubmitButtons} from "./EventSubmissionFormV2";
import * as Sentry from "@sentry/nextjs";

const ImageStep = ({
   initialValues,
   validationSchema,
   selectedImage,
   setSelectedImage,
   onSubmit,
   onClickPrev,
}) => {
    const imageRef = React.useRef();
    const [imageValidationMessage, setImageValidationMessage] = useState('');
    const [data, setData, clearData] = useLocalStorage('eventData', {});

    useEffect(() => {
        if (!data.imageBase64) {
            return
        }

        if (!!data.imageBase64 && !!Object.keys(selectedImage).length) {
            return
        }

        removeImageFromLocalStorage()
    }, [data.imageBase64]);

    const handleImageSelected = (e, setFieldValue) => {
        const file = e.currentTarget.files[0];

        if (!file) {
            removeImage(setFieldValue)
            return;
        }

        const size = (file.size / (1024 * 1024)).toFixed(2);
        if (size >= 5) {
            setImageValidationMessage(
                i18n.t('EventSubmissionForm.validation.tooLargeImage')
            );

            removeImage(setFieldValue)
            return
        } else {
            setImageValidationMessage('');
            saveImage(setFieldValue, e.target.value, file)
        }

        if (file) {
            // This creates a resized, smaller copy of the image, and saves it to form state + local storage ('imageBase64').
            // The resized image is then persistent and displayed throughout the form.

            const reader = new FileReader();

            reader.onload = function (e) {
                try {
                    const image = new Image();

                    image.src = e.target.result;

                    image.onload = function () {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const maxWidth = 300;
                        const maxHeight = 300;

                        // Calculate the new dimensions while maintaining the aspect ratio
                        let newWidth, newHeight;
                        if (image.width > image.height) {
                            newWidth = maxWidth;
                            newHeight = (image.height / image.width) * maxWidth;
                        } else {
                            newHeight = maxHeight;
                            newWidth = (image.width / image.height) * maxHeight;
                        }

                        // Set the canvas dimensions
                        canvas.width = newWidth;
                        canvas.height = newHeight;

                        // Draw the image onto the canvas with the new dimensions
                        ctx.drawImage(image, 0, 0, newWidth, newHeight);

                        // Convert the canvas content to a data URL
                        const resizedDataUrl = canvas.toDataURL('image/jpeg');

                        // Save to state and local storage
                        setFieldValue("imageBase64", resizedDataUrl);
                        setData({...data, imageBase64: resizedDataUrl});
                    };
                } catch (error) {
                    console.error(error)
                    Sentry.withScope(scope => {
                        scope.setTags({'failed-event-submission': 'handleImageSelected in ImageStep'})
                        Sentry.captureException(error)
                    })
                }
            }
            reader.readAsDataURL(file);
        }
    };

    const saveImage = (setFieldValue, image, file) => {
        setFieldValue('image', image);
        setFieldValue('selectedImage', image);
        setFieldValue('file', file);
        setSelectedImage({
            image: image,
            file: file,
        })
    }
    const removeImage = (setFieldValue) => {
        setFieldValue('file', '')
        setFieldValue('image', '')
        setFieldValue('selectedImage', '')
        setFieldValue('imageBase64', '')
        setSelectedImage({});
        removeImageFromLocalStorage();
    }

    const removeImageFromLocalStorage = () => {
        setData({...data, imageBase64: '', image: '', file: ''})
    }

    const deleteImage = (e, setFieldValue) => {
        e.preventDefault();
        removeImage(setFieldValue);
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >{(
            {
                setFieldValue,
            }) => {
            return (
                <Form className={styles['EventSubmissionForm--V2']}>
                    <legend
                        className={
                            styles[
                                'EventSubmissionForm__SectionHeader'
                                ]
                        }
                    >
                        {i18n.t(
                            'EventSubmissionForm.sectionHeaderImageUpload'
                        )}
                    </legend>
                    <InputGroupV2
                        name="image"
                        label={selectedImage.file?.name ? selectedImage.file.name : i18n.t(
                            'EventSubmissionFormV2.labels.chooseImage'
                        )}
                        type="file"
                        innerRef={imageRef}
                        validate={() => {
                            if (imageRef.current?.files[0]?.name?.length > 50) {
                                return i18n.t('EventSubmissionForm.validation.filenameLength');
                            }
                        }}
                        infoMessage={i18n.t(
                            'EventSubmissionForm.chooseImageInfo'
                        )}
                        accept=".jpeg, .jpg, .png"
                        onChange={(e) =>
                            handleImageSelected(e, setFieldValue)
                        }
                        extraErrorMessage={imageValidationMessage}
                    />
                    {!!data.imageBase64 && (
                        <div className={styles["EventSubmissionForm__ImagePreviewContainer"]}>
                            <img
                                className={styles["EventSubmissionForm__ImagePreview"]}
                                src={data.imageBase64}
                                alt=""
                            />

                            <button
                                className={styles["EventSubmissionForm__DeleteImage"]}
                                onClick={(e) => deleteImage(e, setFieldValue)}>
                                {i18n.t(
                                    'EventSubmissionFormV2.buttons.deleteImage'
                                )}
                            </button>
                        </div>
                    )}
                    <InputGroupV2
                        name="photoCredit"
                        placeholder={i18n.t(
                            'EventSubmissionForm.labels.photoCredit'
                        )}
                        type="text"
                    />
                    <InputGroup
                        name="photoUploadAllowed"
                        type="checkbox"
                        i18nlabel="EventSubmissionForm.labels.photoUploadAllowed"
                        items={[
                            {
                                value: 'photoUploadAllowed',
                                label: i18n.t(
                                    'EventSubmissionForm.labels.photoUploadAllowed'
                                ),
                            },
                        ]}
                    />
                    <PrevAndSubmitButtons onClickPrev={onClickPrev}/>
                </Form>
            )
        }}
        </Formik>
    )
}

export default ImageStep;
